import { motion } from "framer-motion"
import { marked } from "marked"
import { useEffect, useState } from "react"
import DOMPurify from "dompurify"
import { useNavigate, useParams } from "react-router-dom"
import { Button } from "~/components/Button"
import { GlobalNav } from "~/components/GlobalNav"
import { NumericalInput } from "~/components/NumericalInput"
import { TokenPanel } from "~/components/TokenPanel"
import { VotePanel } from "~/components/VotePanel"
import { MarkdownContent } from "~/components/MarkdownContent"
import { TOKENS } from "~/constants"
import { IMPORTANCE, type ImportanceLevel } from "~/constants/importance"
import { formatLargeBalance } from "~/utils/formatLargeBalance"
import { useAccount } from "~/hooks/useAccount"
import { useGameData, type GameData } from "~/hooks/useGameData"
import { useTaskCreation } from "~/hooks/useTaskCreation"
import { useBalance } from "~/hooks/useBalance"
import { useDagProgram } from "~/hooks/useDagProgram"
import { api } from "~/services/api"

export default function TaskCreate() {
  const { gameId } = useParams<{ gameId: string }>()
  const { game, isLoading, error } = useGameData({ gameId: gameId! })
  const navigate = useNavigate()

  if (isLoading || !game) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-white">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <GlobalNav />
        <div className="flex justify-center items-center h-96">
          <p className="text-red-500">Error: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="">
      <GlobalNav />

      <div className="pt-6 px-10 max-w-screen-md mx-auto">
        <Panel game={game} gameId={gameId || ''} />
      </div>
    </div>
  )
}

interface PanelProps {
  game: GameData;
  gameId: string;
}

const Panel = ({ game, gameId }: PanelProps) => {
  const [priority, setPriority] = useState<ImportanceLevel>('MEDIUM')
  const [readmeMode, setReadmeMode] = useState<'edit' | 'preview'>('edit')
  const [task, setTask] = useState({
    title: "",
    description: "",
    priority: "",
    readme: "",
    readmeHtml: "",
  })
  const { account, isAuthenticated, user } = useAccount()
  const navigate = useNavigate()
  const { 
    createTask,
    retryTaskCreation,
    isLoading,
    isConfirming,
    isCreatingTask,
    error: taskError,
    transactionResult,
    clearError
  } = useTaskCreation()
  const { getDevPoolBalance } = useBalance()
  
  // Get dev pool balance for this game's token
  const devPoolBalanceData = getDevPoolBalance(game.token.address)

  const onCreate = async () => {
    if (!account?.address) {
      alert('Please connect your wallet first')
      return
    }
    
    if (!task.title || !task.description) {
      alert('Please fill in all required fields')
      return
    }

    try {
      const bountyBps = IMPORTANCE[priority].bountyBps
      const result = await createTask({
        title: task.title,
        description: task.description,
        bountyBps,
        gameId,
        tokenAddress: game.token.address
      })
      
      // Navigate to the newly created proposal
      if (result.proposal) {
        navigate(`/games/${gameId}/tasks/${result.proposal.proposalId}`)
      }
    } catch (error) {
      console.error('Failed to create task:', error)
      // Don't use alert, let the error be shown in the UI
    }
  }

  const onRetry = async () => {
    try {
      const result = await retryTaskCreation()
      
      // Navigate to the newly created proposal
      if (result.proposal) {
        navigate(`/games/${gameId}/tasks/${result.proposal.proposalId}`)
      }
    } catch (error) {
      console.error('Failed to retry task creation:', error)
    }
  }

  useEffect(() => {
    const sanitized = DOMPurify.sanitize(marked.parse(task.description) as string);
    setTask({ ...task, description: sanitized });
  }, [task.description]);

  return (
    <div className="flex flex-col gap-8 p-6 rounded-lg border border-solid border-[#212425]">
      <div className="flex items-center self-stretch relative gap-2">
        <p className="text-2xl font-semibold text-left text-white">New Task</p>
      </div>

      {/* Dev Pool Info */}
      <div className="flex flex-col gap-2 p-4 rounded-lg bg-[#1a1b1b] border border-solid border-[#212425]">
        <div className="flex items-center justify-between">
          <p className="text-base font-medium text-white">Dev Pool Balance</p>
          {devPoolBalanceData.isLoading && (
            <div className="text-sm text-[#6b6d6e]">Loading...</div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-[#9c9e9e]">Available for rewards</span>
          <div className="text-right">
            <div className="text-lg font-semibold text-white">
              {formatLargeBalance(devPoolBalanceData.balance)} {game.token.symbol}
            </div>
            <div className="text-sm text-[#9c9e9e]">
              ≈ ${(devPoolBalanceData.balance * game.token.price).toFixed(2)}
            </div>
          </div>
        </div>
        {devPoolBalanceData.error && (
          <div className="text-sm text-red-400">
            Error loading dev pool balance: {devPoolBalanceData.error.message}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center self-stretch relative gap-2">
          <p className="text-xl text-left text-white">Importance</p>
        </div>
        <div className={`flex justify-start items-start w-full p-0.5 rounded-lg bg-[#212425]`}>
          {Object.entries(IMPORTANCE).map(([key, option]) => (
            <button
              key={key}
              onClick={() => setPriority(key as ImportanceLevel)}
              className="relative flex-1"
            >
              {priority === key && (
                <motion.div 
                  layoutId={`task-priority`}
                  className="absolute inset-0 flex justify-start items-start flex-grow">  
                  <div className={`flex justify-center items-center flex-grow h-20 relative px-3 rounded-lg bg-[#60e25e]/[0.15]`}/>
                </motion.div>
              )}
              <div className="relative z-10 flex justify-center items-center flex-grow h-20 px-3 rounded-lg">
                <div className="flex flex-col gap-3">
                  <p className={`flex-grow-0 flex-shrink-0 text-[13px] font-bold transition-colors ${priority === key ? 'text-[#60e25e]' : 'text-[#9c9e9e]'}`}>
                    {option.label}
                  </p>
                  <div className="flex flex-col gap-1">
                    <p className="text-[11px] font-medium text-[#9c9e9e]" style={{ wordSpacing: '-5px' }}>
                      {formatLargeBalance(devPoolBalanceData.balance * option.reward)} {game.token.symbol}
                    </p>
                    <p className="text-[13px] font-medium text-[#9c9e9e]" style={{ wordSpacing: '-5px' }}>
                      ≈ ${(devPoolBalanceData.balance * option.reward * game.token.price).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center self-stretch relative gap-2">
          <p className="text-xl text-left text-white">Task Info</p>
        </div>
        <div className="flex flex-col gap-4 p-4 rounded-lg border border-solid border-[#212425]">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-left text-white">
              Title
            </p>
            <input
              value={task.title}
              onChange={(e) => setTask({ ...task, title: e.target.value })}
              className="px-4 py-3 rounded-lg bg-white/5 border border-solid border-[#212425] text-sm font-medium text-left text-white"
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <p className="text-base font-medium text-left text-white">
                Description
              </p>
              <button className="text-xs font-medium text-left text-white" onClick={() => setReadmeMode(readmeMode === 'edit' ? 'preview' : 'edit')}>
                <span className={readmeMode === 'edit' ? 'text-[#60e25e]' : 'text-[#6b6d6e]'}>Edit</span> / <span className={readmeMode === 'preview' ? 'text-[#60e25e]' : 'text-[#6b6d6e]'}>Preview</span>
              </button>
            </div>
            <div className="">
              {readmeMode === 'edit' ? (
                <textarea
                  value={task.description}
                  onChange={(e) => setTask({ ...task, description: e.target.value })}
                  placeholder="Enter task description in markdown format"
                  className="w-full h-[300px] px-3 py-2 text-white bg-[#212425] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#60e25e] focus:ring-opacity-50"
                />
              ) : (
                <div className="p-4 rounded-lg bg-[#212425]/50">
                  <MarkdownContent content={task.description} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Authentication Status Messages */}
      {!isAuthenticated && (
        <div className="px-4 py-3 rounded-lg bg-yellow-900/20 border border-solid border-yellow-500/50">
          <p className="text-sm font-medium text-yellow-400">
            Please sign in with your wallet to create a task. You'll also need to connect your GitHub account.
          </p>
        </div>
      )}

      {isAuthenticated && user && !user.githubConnected && (
        <div className="px-4 py-3 rounded-lg bg-yellow-900/20 border border-solid border-yellow-500/50">
          <p className="text-sm font-medium text-yellow-400">
            Please connect your GitHub account to create a task with repository integration.
          </p>
        </div>
      )}

      {/* Error Display */}
      {taskError && (
        <div className="px-4 py-3 rounded-lg bg-red-900/20 border border-solid border-red-500/50">
          <div className="flex justify-between items-start">
            <p className="text-sm font-medium text-red-400">
              {taskError}
            </p>
            <button
              onClick={clearError}
              className="text-red-400 hover:text-red-300 ml-2"
            >
              ×
            </button>
          </div>
          {transactionResult && (
            <div className="mt-3 flex gap-2">
              <Button
                onClick={onRetry}
                loading={isCreatingTask}
                size="sm"
                type="secondary"
              >
                Retry API Call
              </Button>
            </div>
          )}
        </div>
      )}

      <Button 
        loading={isLoading || isConfirming || isCreatingTask} 
        onClick={onCreate}
        disabled={!isAuthenticated || (user?.githubConnected === false)}
      >
        {isConfirming ? 'Confirming Transaction...' : isCreatingTask ? 'Creating Task...' : 'Create Task'}
      </Button>
    </div>
  )
}
